import { useDeferredValue, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { TaskStatusCard } from "../components/TaskStatusCard";
import { TaskWizard } from "../components/TaskWizard";
import { useTaskFeed } from "../context/TaskFeedContext";
import { formatDate, taskIsActive } from "../utils/format";

type TaskFilter = "all" | "active" | "success" | "failed";

const filters: { value: TaskFilter; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "active", label: "进行中" },
  { value: "success", label: "已完成" },
  { value: "failed", label: "需处理" },
];

export function TasksPage() {
  const feed = useTaskFeed();
  const [filter, setFilter] = useState<TaskFilter>("all");
  const [query, setQuery] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const deferredQuery = useDeferredValue(query.trim());
  const filtered = feed.tasks.filter((task) => {
    if (deferredQuery && !String(task.id).includes(deferredQuery)) return false;
    if (filter === "active") return taskIsActive(task);
    if (filter === "success") return task.status === "completed";
    if (filter === "failed") return ["partial_failed", "failed", "canceled"].includes(task.status);
    return true;
  });

  return (
    <div className="page-stack">
      <section className="task-command-bar">
        <div>
          <span className="eyebrow">SERVER-SIDE RECOVERY</span>
          <h2>关闭页面，任务仍会继续</h2>
          <p>活动任务自动轮询；回到页面或切换标签时会立即刷新。</p>
        </div>
        <div className="task-command-bar__actions">
          <div className="task-command-bar__sync">
            <span className={feed.activeTasks.length ? "sync-light is-active" : "sync-light"}><i /></span>
            <span><b>{feed.activeTasks.length ? `${feed.activeTasks.length} 个任务在线` : "当前空闲"}</b><small>{feed.lastUpdated ? `更新于 ${formatDate(feed.lastUpdated.toISOString())}` : "等待首次同步"}</small></span>
            <button className="icon-button" onClick={feed.refresh} aria-label="立即刷新"><Icon name="refresh" size={19} /></button>
          </div>
          <button className="button button--lime" onClick={() => setComposerOpen(true)}><Icon name="tasks" size={18} />创建任务</button>
        </div>
      </section>

      <section className="task-browser">
        <div className="task-browser__tools">
          <div className="filter-tabs" role="tablist" aria-label="任务筛选">
            {filters.map((item) => <button key={item.value} role="tab" aria-selected={filter === item.value} className={filter === item.value ? "is-active" : ""} onClick={() => setFilter(item.value)}>{item.label}</button>)}
          </div>
          <label className="search-field" htmlFor="task-search"><Icon name="search" size={18} /><input id="task-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="按任务编号查找" inputMode="numeric" /></label>
        </div>

        {feed.loading ? <LoadingRows count={5} /> : null}
        {feed.error ? <ResourceError message={feed.error} onRetry={feed.refresh} /> : null}
        {feed.unsupported ? <EmptyState icon="tasks" title="当前环境没有任务历史接口" description="旁路前端不会模拟任务。升级后端后，历史记录和刷新恢复会自动启用。" /> : null}
        {!feed.loading && !feed.error && !feed.unsupported && !feed.tasks.length ? <EmptyState icon="tasks" title="还没有任务记录" description="选择服务端账号批次、执行方式和集成路径后创建第一个任务。" action={<button className="button button--primary" onClick={() => setComposerOpen(true)}>创建第一个任务</button>} /> : null}
        {!feed.loading && !feed.error && feed.tasks.length && !filtered.length ? <EmptyState compact icon="search" title="没有符合条件的任务" description="调整状态筛选或清空任务编号。" /> : null}
        {filtered.length ? <div className="task-card-grid">{filtered.map((task) => <TaskStatusCard key={task.id} task={task} />)}</div> : null}
      </section>
      <TaskWizard open={composerOpen} onClose={() => setComposerOpen(false)} />
    </div>
  );
}
